import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FolderIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { ProjectFolderSelector } from "../../utils/moduleSelectors/projectFolderSelector";
import { DrawingSelector } from "../../utils/moduleSelectors/drawingSelector";
import {
  DOCUMENT_UPLOAD_PREVIEW_CHILD_Z_INDEX,
} from "./DocumentUploadPreviewDialog";
import {
  getDocumentPlacementSession,
  requestFullCanvasDocumentPlacement,
  shouldSkipAutoOpenPlacement,
  clearSkipAutoOpenPlacement,
} from "../drawings/drawingDocumentPlacementSession";
import {
  locationToPath,
  sanitizeReturnPath,
} from "../../../utils/authReturnUrl";
import {
  bindUploadEntryToFolder,
  folderFromSelectorEvent,
  folderUploadFallback,
} from "./document-upload-service";

/**
 * Optional preview extras for DocumentUploadPreviewDialog / DirectImageUploadButton.
 *
 * @typedef {Object} DocumentUploadPreviewOptions
 * @property {boolean} [folderSupport] - Show project folder selector above file list
 * @property {string|number} [folderProjectId] - Project id for folder selector
 * @property {boolean} [drawingSupport] - Per-file drawing + position picker
 * @property {{ id?: string|number }} [project] - Project used by drawing selector
 * @property {{ id?: string|number }} [selectedFolder] - Initial folder selection
 * @property {{ id?: string|number, title?: string }} [defaultDrawing] - Pre-select / auto-open drawing
 * @property {boolean} [lockDrawing] - Hide drawing selector (linked drawing only — place pin)
 * @property {boolean} [autoOpenPositionPicker] - Open pin placement when drawing is set without position
 * @property {boolean} [enableSketch] - Allow optional sketch/markering after placing pin
 * @property {boolean} [fullCanvasPlacement] - Place pin on full Tegninger viewer (default true with drawingSupport)
 * @property {string|number|null} [suspendFillChecklistId] - Checklist fill dialog id to reopen after place
 */

function resolveFolderProjectId(previewOptions, module, moduleParam) {
  if (previewOptions?.folderProjectId != null && previewOptions.folderProjectId !== "") {
    return previewOptions.folderProjectId;
  }
  if (module === "project" && moduleParam != null && moduleParam !== "") {
    return moduleParam;
  }
  return previewOptions?.project?.id ?? null;
}

function resolveDrawingProjectId(previewOptions, module, moduleParam) {
  return (
    previewOptions?.project?.id ||
    (module === "project" ? moduleParam : null) ||
    previewOptions?.folderProjectId ||
    null
  );
}

/**
 * Shared renderers for upload preview (folder, drawing mark, full-canvas pin placement).
 */
export function useDocumentUploadPreviewRenderers({
  previewOptions,
  filesToUpload,
  setFilesToUpload,
  module,
  moduleParam,
  isUploading = false,
  onFolderChange,
  onPlacementStarted,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentFolder, setCurrentFolder] = useState(
    previewOptions?.selectedFolder || null
  );
  const [drawings, setDrawings] = useState({});
  const autoOpenedForIndexRef = useRef(new Set());
  const filesRef = useRef(filesToUpload);
  filesRef.current = filesToUpload;

  const folderProjectId = resolveFolderProjectId(previewOptions, module, moduleParam);
  const drawingProjectId = resolveDrawingProjectId(previewOptions, module, moduleParam);
  const { module: fallbackModule, moduleParam: fallbackParam } =
    folderUploadFallback(module, moduleParam, folderProjectId);
  const defaultDrawing = previewOptions?.defaultDrawing || null;
  const lockDrawing = Boolean(previewOptions?.lockDrawing && defaultDrawing?.id);
  const autoOpenPositionPicker = Boolean(previewOptions?.autoOpenPositionPicker);
  const enableSketch = Boolean(previewOptions?.enableSketch);
  const fullCanvasPlacement =
    previewOptions?.fullCanvasPlacement !== false && Boolean(previewOptions?.drawingSupport);

  const showFolder = Boolean(previewOptions?.folderSupport && folderProjectId);
  const showDrawing = Boolean(
    previewOptions?.drawingSupport && previewOptions?.project && drawingProjectId
  );

  useEffect(() => {
    setCurrentFolder(previewOptions?.selectedFolder || null);
  }, [previewOptions?.selectedFolder?.id]);

  // Apply default/linked drawing to new files and drawing state
  useEffect(() => {
    if (!showDrawing || !defaultDrawing?.id || !filesToUpload.length) return;

    setDrawings((prev) => {
      const next = { ...prev };
      let changed = false;
      filesToUpload.forEach((file, index) => {
        if (!next[index] && (file.drawing_id == null || file.drawing_id === defaultDrawing.id)) {
          next[index] = defaultDrawing;
          changed = true;
        }
      });
      return changed ? next : prev;
    });

    setFilesToUpload((prev) => {
      let changed = false;
      const updated = prev.map((entry) => {
        if (entry.drawing_pin_declined) return entry;
        if (entry.drawing_id) return entry;
        changed = true;
        return {
          ...entry,
          drawing_id: defaultDrawing.id,
          drawing_position_x: entry.drawing_position_x ?? null,
          drawing_position_y: entry.drawing_position_y ?? null,
          drawing_rotation: entry.drawing_rotation ?? 0,
        };
      });
      return changed ? updated : prev;
    });
  }, [showDrawing, defaultDrawing?.id, filesToUpload.length, setFilesToUpload]);

  const startFullCanvasPlacement = useCallback(
    (index) => {
      if (isUploading) return;
      const files = filesRef.current;
      const file = files[index];
      if (!file) return;

      const drawing =
        drawings[index] ||
        (file.drawing_id
          ? defaultDrawing?.id === file.drawing_id
            ? defaultDrawing
            : { id: file.drawing_id }
          : defaultDrawing);

      if (!drawing?.id) {
        toast.error(t("documentUpload.preview.selectDrawingFirst"));
        return;
      }

      const mode = requestFullCanvasDocumentPlacement({
        drawingId: drawing.id,
        fileIndex: index,
        files: files.map((entry) => ({ ...entry })),
        module,
        moduleParam,
        wantSketch: Boolean(file.want_sketch),
        returnPath: sanitizeReturnPath(locationToPath(location)),
        suspendFillChecklistId: previewOptions?.suspendFillChecklistId ?? null,
        suspendModuleDetailId: previewOptions?.suspendModuleDetailId ?? null,
        suspendDeviationId: previewOptions?.suspendDeviationId ?? null,
        title: file.title || file.file?.name || "",
        source: "direct",
        navigate,
      });

      if (!mode) {
        toast.error(t("documentUpload.preview.drawingLoadFailed"));
        return;
      }

      autoOpenedForIndexRef.current.add(index);
      onPlacementStarted?.(index);
    },
    [
      drawings,
      defaultDrawing,
      enableSketch,
      isUploading,
      location.pathname,
      location.search,
      module,
      moduleParam,
      navigate,
      onPlacementStarted,
      previewOptions?.suspendFillChecklistId,
      previewOptions?.suspendModuleDetailId,
      previewOptions?.suspendDeviationId,
      t,
    ]
  );

  // Auto-start full-canvas place mode for linked drawing when position is missing
  useEffect(() => {
    if (!showDrawing || !autoOpenPositionPicker || !fullCanvasPlacement || isUploading) {
      return;
    }
    const session = getDocumentPlacementSession();
    if (session?.status === "placing") return;

    const index = filesToUpload.findIndex(
      (file, i) =>
        (file.drawing_id || drawings[i]?.id || defaultDrawing?.id) &&
        (file.drawing_position_x == null || file.drawing_position_y == null) &&
        !autoOpenedForIndexRef.current.has(i) &&
        !shouldSkipAutoOpenPlacement(module, moduleParam, i)
    );
    if (index < 0) return;

    startFullCanvasPlacement(index);
  }, [
    showDrawing,
    autoOpenPositionPicker,
    fullCanvasPlacement,
    isUploading,
    filesToUpload,
    drawings,
    defaultDrawing?.id,
    module,
    moduleParam,
    startFullCanvasPlacement,
  ]);

  // Clear auto-opened indices when the list is emptied (new uploads reuse index 0)
  useEffect(() => {
    if (filesToUpload.length === 0) {
      autoOpenedForIndexRef.current.clear();
      // Allow auto-open again for a fresh upload batch
      clearSkipAutoOpenPlacement(module, moduleParam, 0);
    }
  }, [filesToUpload.length, module, moduleParam]);

  const handleFolderChange = useCallback(
    (event) => {
      const folder = folderFromSelectorEvent(event);
      setCurrentFolder(folder);
      setFilesToUpload((prev) =>
        prev.map((entry) =>
          bindUploadEntryToFolder(entry, folder, fallbackModule, fallbackParam)
        )
      );
      if (onFolderChange) {
        onFolderChange(folder);
      }
    },
    [onFolderChange, setFilesToUpload, fallbackModule, fallbackParam]
  );

  const handleDrawingChange = useCallback(
    (index, drawing) => {
      if (lockDrawing) return;
      setDrawings((prev) => ({ ...prev, [index]: drawing }));
      setFilesToUpload((prev) => {
        const updated = [...prev];
        if (!updated[index]) return prev;
        updated[index] = {
          ...updated[index],
          drawing_id: drawing?.id || null,
          drawing_position_x: null,
          drawing_position_y: null,
          drawing_rotation: 0,
          drawing_snapshot_blob: null,
        };
        return updated;
      });
      autoOpenedForIndexRef.current.delete(index);
    },
    [lockDrawing, setFilesToUpload]
  );

  const handleWantSketchChange = useCallback(
    (index, checked) => {
      setFilesToUpload((prev) => {
        const updated = [...prev];
        if (!updated[index]) return prev;
        updated[index] = {
          ...updated[index],
          want_sketch: checked,
        };
        return updated;
      });
    },
    [setFilesToUpload]
  );

  const enhancePendingEntries = useCallback(
    (entries) => {
      return entries.map((entry) => {
        let next = { ...entry };
        if (showFolder) {
          next = bindUploadEntryToFolder(
            next,
            currentFolder,
            fallbackModule,
            fallbackParam
          );
        }
        if (
          showDrawing &&
          defaultDrawing?.id &&
          !next.drawing_id &&
          !next.drawing_pin_declined
        ) {
          next.drawing_id = defaultDrawing.id;
        }
        if (enableSketch && next.want_sketch == null) {
          next.want_sketch = false;
        }
        return next;
      });
    },
    [
      currentFolder,
      showFolder,
      showDrawing,
      defaultDrawing?.id,
      enableSketch,
      fallbackModule,
      fallbackParam,
    ]
  );

  const renderBeforeList = useMemo(() => {
    if (!showFolder) {
      return undefined;
    }
    function DocumentUploadPreviewBeforeList() {
      return (
        <div className="mb-3 flex-shrink-0 rounded-lg bg-blue-50 p-3 ring-1 ring-inset ring-blue-100 sm:p-4">
          <div className="mb-2 flex items-center">
            <FolderIcon className="mr-2 h-5 w-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">
              {t("documentUpload.preview.folderLabel")}
            </span>
          </div>
          <ProjectFolderSelector
            projectId={folderProjectId}
            selected={currentFolder}
            onChange={handleFolderChange}
            zIndex={DOCUMENT_UPLOAD_PREVIEW_CHILD_Z_INDEX}
          />
          {currentFolder ? (
            <p className="mt-1 text-xs text-blue-600">
              {t("documentUpload.preview.folderSelectedHint")}
            </p>
          ) : null}
        </div>
      );
    }
    return DocumentUploadPreviewBeforeList;
  }, [currentFolder, folderProjectId, handleFolderChange, showFolder, t]);

  const renderFileExtra = useMemo(() => {
    if (!showDrawing) {
      return undefined;
    }
    function DocumentUploadPreviewFileExtra(file, index) {
      const selectedDrawing =
        drawings[index] ||
        (file.drawing_id
          ? defaultDrawing?.id === file.drawing_id
            ? defaultDrawing
            : { id: file.drawing_id }
          : null);

      return (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            {t("documentUpload.preview.drawingLabel")}
          </label>
          {lockDrawing && selectedDrawing ? (
            <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900">
              {selectedDrawing.title ||
                t("documentUpload.preview.linkedDrawing", {
                  id: selectedDrawing.id,
                })}
              <p className="mt-0.5 text-xs text-blue-700">
                {t("documentUpload.preview.linkedDrawingHint")}
              </p>
            </div>
          ) : (
            <DrawingSelector
              projectId={drawingProjectId}
              selected={selectedDrawing}
              onChange={(drawing) => handleDrawingChange(index, drawing)}
              placeholder={t("documentUpload.preview.drawingPlaceholder")}
              disabled={isUploading}
              zIndex={DOCUMENT_UPLOAD_PREVIEW_CHILD_Z_INDEX}
            />
          )}
          {selectedDrawing ? (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {t("documentUpload.preview.drawingPositionLabel")}
              </label>
              {file.drawing_position_x !== null &&
              file.drawing_position_y !== null &&
              file.drawing_position_x !== undefined &&
              file.drawing_position_y !== undefined ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-gray-600">
                    {t("documentUpload.preview.drawingPosition", {
                      x: Number(file.drawing_position_x).toFixed(2),
                      y: Number(file.drawing_position_y).toFixed(2),
                    })}
                  </span>
                  {file.drawing_snapshot_blob ? (
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                      {t("documentUpload.preview.sketchAttached")}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => startFullCanvasPlacement(index)}
                    disabled={isUploading}
                    className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t("documentUpload.preview.changeDrawingPosition")}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => startFullCanvasPlacement(index)}
                  disabled={isUploading}
                  className="w-full rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t("documentUpload.preview.pickDrawingPosition")}
                </button>
              )}
              {enableSketch ? (
                <label className="flex cursor-pointer items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={Boolean(file.want_sketch)}
                    onChange={(e) => handleWantSketchChange(index, e.target.checked)}
                    disabled={isUploading}
                  />
                  <span>
                    <span className="font-medium">
                      {t("documentUpload.preview.enableSketch")}
                    </span>
                    <span className="mt-0.5 block text-xs text-gray-500">
                      {t("documentUpload.preview.enableSketchHintFullCanvas")}
                    </span>
                  </span>
                </label>
              ) : null}
              <p className="text-xs text-gray-500">
                {t("documentUpload.preview.fullCanvasPlacementHint")}
              </p>
            </div>
          ) : null}
        </div>
      );
    }
    return DocumentUploadPreviewFileExtra;
  }, [
    drawings,
    drawingProjectId,
    handleDrawingChange,
    handleWantSketchChange,
    startFullCanvasPlacement,
    isUploading,
    showDrawing,
    lockDrawing,
    defaultDrawing,
    enableSketch,
    t,
  ]);

  // No nested mini pick-position dialog — placement happens on full Tegninger viewer.
  const renderAfterList = useMemo(() => undefined, []);

  return {
    enhancePendingEntries,
    renderBeforeList,
    renderFileExtra,
    renderAfterList,
    startFullCanvasPlacement,
  };
}
